/**
 * 문서 AI 초안 (모든 문서 공통, Amendment A27).
 * 원칙: 상담 대화·기록에서 판단할 수 있는 칸은 AI가 먼저 쓰고, 상담자는 확인·수정·추가만 한다.
 * - 빈 칸([자료 부족]/[상담자 직접 작성 필요])과 이전 AI 초안만 채운다. 상담자가 쓴 내용·자료로 채운 내용은 건드리지 않는다.
 * - AI가 쓴 칸에는 "[AI 초안 — 확인 필요]" 표시가 붙고, 문서를 승인하면 표시가 지워진다.
 * - 상담자 성찰·신학적 성찰도 초안을 쓰되, 상담자의 발언·개입에서 드러난 것만 근거로 하고 추정임을 밝힌다.
 * - 보내는 자료: 내담자 코드만 (실명·연락처·생년·별칭 없음).
 */
import type { DocumentArtifact } from '../schema/v1';
import { BEGIN, END } from './prompts';
import { parseJsonLoose, str } from './parse';
import { AI_DRAFT_MARK, isAiDraftMark, upsertDraftField } from './report';
import { DIALOGUE_APPENDIX, DIALOGUE_QA_EXPECTED, DIALOGUE_QA_MINE } from '../workflow/documents';

export const DOC_DRAFT_VERSIONS = {
  packet: 'DOC_DRAFT_PACKET_V1',
  output: 'DOC_DRAFT_V1',
  task: 'doc-draft-task-0.1',
} as const;

export type DraftSpec =
  /** 이 제목의 칸이 비어 있거나 이전 AI 초안이면 채운다 */
  | { key: string; mode: 'fill'; heading: string; ask: string }
  /** 큰 항목(section) 안, after 칸 뒤에 새 칸을 넣는다 (서술 문단·기법 정리) */
  | { key: string; mode: 'add'; section: string; after?: string; label: string; ask: string; sectionTitle?: string };

const TECHNIQUES_ASK =
  '사용한 상담기법을 기법마다 한 덩어리로: "기법명 — 이론(대표 학자, 예: 인간중심 Carl Rogers, 인지치료 Aaron Beck) — 어느 회기·어느 발언(번호)에서 어떻게 썼는지 — 내담자 반응 — 효과와 한계". 근거 없는 기법은 쓰지 않는다';
const PLAN_ASK =
  '다음 회기에 쓸 기법·접근을 2~4개: "기법 — 이론(대표 학자) — 왜 이 내담자에게 — 구체적인 첫 질문이나 진행 예 — 주의할 점"';

export const DOC_DRAFT_SPECS: Record<DocumentArtifact['documentType'], DraftSpec[]> = {
  session_record: [
    {
      key: 'counselingContent',
      mode: 'fill',
      heading: '### 상담 내용',
      ask: '이번 회기 흐름·주요 내용·상담자 개입과 내담자 반응을 5~8문장',
    },
    {
      key: 'nextSessionPlan',
      mode: 'fill',
      heading: '### 다음 회기 계획',
      ask: '다음 회기 목표·확인할 것·사용할 기법(이론·학자)',
    },
    {
      key: 'remarks',
      mode: 'fill',
      heading: '### 비고',
      ask: '위험·의뢰·과제·특이사항이 있으면 간단히, 없으면 "특이사항 없음"',
    },
  ],
  case_report: [
    {
      key: 'presenting',
      mode: 'fill',
      heading: '### 주호소 정리',
      ask: '주호소와 내담자의 표현을 바탕으로 2~4문장',
    },
    {
      key: 'observation',
      mode: 'fill',
      heading: '### 3. 인상 및 행동 관찰',
      ask: '대화에서 드러난 말하는 방식·정서·태도 (관찰 가능한 것만)',
    },
    {
      key: 'testInterpretation',
      mode: 'fill',
      heading: '#### 2) 상담자의 해석',
      ask: '검사 결과가 있을 때만 해석, 없으면 "실시한 검사 없음"',
    },
    {
      key: 'family',
      mode: 'fill',
      heading: '#### 1) 가족구성원',
      ask: '내담자가 말한 가족 구성과 관계 (사람 이름 대신 관계로)',
    },
    { key: 'social', mode: 'fill', heading: '### 6. 사회적 관계', ask: '가족 외 관계·직장/학교생활·공동체' },
    {
      key: 'attachment',
      mode: 'fill',
      heading: '#### 1) 초기의 애착 대상과의 관계',
      ask: '어린 시절 양육자와의 관계에 대해 말한 것 (없으면 "자료 부족 — 다음 회기 탐색 필요")',
    },
    {
      key: 'lifeEvents',
      mode: 'fill',
      heading: '#### 2) 청소년 및 성인기',
      ask: '청소년·성인기 주요 사건, 성취·실패 경험',
    },
    {
      key: 'recentDev',
      mode: 'fill',
      heading: '#### 3) 최근 문제의 발생',
      ask: '최근 문제가 시작되고 유지되는 데 관련된 정보',
    },
    { key: 'strengths', mode: 'fill', heading: '### 8. 내담자의 강점과 약점', ask: '강점과 약점' },
    { key: 'faithGod', mode: 'fill', heading: '#### 1) 내담자의 하나님 이해', ask: '내담자가 하나님에 대해 말한 것' },
    {
      key: 'faithLife',
      mode: 'fill',
      heading: '#### 2) 내담자의 신앙생활에 대한 이해',
      ask: '신앙생활(예배·공동체·사역 등)',
    },
    {
      key: 'faithHuman',
      mode: 'fill',
      heading: '#### 3) 내담자의 인간에 대한 이해',
      ask: '자기와 타인을 어떻게 보는지',
    },
    { key: 'problems', mode: 'fill', heading: '### ① 주호소문제 및 문제행동', ask: '주호소문제와 문제행동' },
    {
      key: 'causes',
      mode: 'fill',
      heading: '### ② 문제행동 이면의 유발요인',
      ask: '유발요인 (신앙 요인 포함), 가설은 가설로',
    },
    { key: 'process', mode: 'fill', heading: '### ③ 문제행동을 촉발·유지시키는 과정', ask: '촉발·유지 과정' },
    {
      key: 'theory',
      mode: 'fill',
      heading: '### 관련 상담 이론에 근거한 통합 가설',
      ask: '메인 이론(대표 학자)으로 ①②③을 잇는 통합 서술: 핵심 가설 한 문장, 이를 지지하는 행동패턴(실제 발화), 이론을 택한 이유, 반증 자료도',
    },
    {
      key: 'selfGod',
      mode: 'fill',
      heading: '#### 1) 상담자 자신의 하나님이해',
      ask: '상담자의 발언·개입에서 드러난 하나님 이해 (추정임을 밝힘)',
    },
    { key: 'selfHuman', mode: 'fill', heading: '#### 2) 상담자 자신의 인간이해', ask: '상담자의 인간 이해 (추정)' },
    { key: 'selfFaith', mode: 'fill', heading: '#### 3) 상담자로서 이해하는 신앙인으로서 자세와 삶', ask: '추정 초안' },
    {
      key: 'selfView',
      mode: 'fill',
      heading: '#### 4) 내담자에 대한 상담자의 신학적 관점 분석',
      ask: '내담자를 신학적으로 어떻게 보고 있는지',
    },
    {
      key: 'chosenTheory',
      mode: 'fill',
      heading: '#### 1) 상담자로서 택한 상담이론',
      ask: '실제 개입에서 드러난 이론(대표 학자)과 택한 이유',
    },
    { key: 'method', mode: 'fill', heading: '#### 2) 사례(내담자)를 위한 상담방법론', ask: '이 사례를 위한 방법론' },
    {
      key: 'dialogue',
      mode: 'fill',
      heading: '### 3. 상담자의 학문(신학과 상담심리학)간 대화',
      ask: '신학과 상담심리학이 이 사례에서 어떻게 만나는지',
    },
    { key: 'agreedGoals', mode: 'fill', heading: '#### 1) 합의한 상담 목표', ask: '내담자의 표현으로 1)2)3)' },
    { key: 'clinicalGoals', mode: 'fill', heading: '#### 2) 임상적 상담 목표', ask: '상담자의 목표와 전략 1)2)3)' },
    {
      key: 'strategy',
      mode: 'fill',
      heading: '### 2. 상담 전략',
      ask: `주요 전략 2~3개를 구체적으로 (방법·근거·어려움과 대처). 그 안에 ${TECHNIQUES_ASK}. 이어서 ${PLAN_ASK}`,
    },
    {
      key: 'verbatimReason',
      mode: 'fill',
      heading: '#### 1) 이 부분을 축어록으로 만든 이유',
      ask: '이 구간을 고른 이유 (임상적 의미)',
    },
    { key: 'clientEval', mode: 'fill', heading: '#### 1) 내담자의 평가', ask: '내담자가 변화나 상담에 대해 말한 것' },
    {
      key: 'evaluation',
      mode: 'fill',
      heading: '#### 2) 상담자의 평가',
      ask: '목표별 진행과 변화, 남은 과제를 근거와 함께',
    },
    {
      key: 'selfEval',
      mode: 'fill',
      heading: '### 2. 상담자 자신에 대한 평가',
      ask: '개입의 강점·아쉬운 점 (발언 번호 근거)',
    },
    { key: 'supervision', mode: 'fill', heading: '### 수퍼비전 질문', ask: '수퍼비전에서 물을 질문 3~5개' },
    { key: 'pastoralEval', mode: 'fill', heading: '### 1. 목회상담으로서 본 상담에 대한', ask: '목회신학적 평가' },
    {
      key: 'pastoralTraits',
      mode: 'fill',
      heading: '### 2. 본 상담을 목회상담이라고 부를 수 있는',
      ask: '목회상담이라고 부를 수 있는 요인과 특징',
    },
  ],
  termination_report: [
    { key: 'changed', mode: 'fill', heading: '#### 1) 변화된 점', ask: '변화된 점' },
    { key: 'remaining', mode: 'fill', heading: '#### 2) 남아 있는 어려움', ask: '남아 있는 어려움' },
    { key: 'after', mode: 'fill', heading: '#### 3) 보호요인 및 종결 후 제언', ask: '보호요인과 종결 후 제언' },
    { key: 'techniques', mode: 'fill', heading: '### 상담 전략 및 주요 개입', ask: TECHNIQUES_ASK },
    {
      key: 'reflection',
      mode: 'fill',
      heading: '### 상담자 소감 및 배운 점',
      ask: '상담자의 소감과 배운 점 초안 (추정임을 밝힘)',
    },
  ],
  verbatim: [
    { key: 'reason', mode: 'fill', heading: '### 이 구간을 선택한 이유', ask: '이 구간의 임상적 의미' },
    { key: 'supervision', mode: 'fill', heading: '### 수퍼비전 질문', ask: '발언 번호를 들어 질문 3~5개' },
    {
      key: 'process',
      mode: 'fill',
      heading: '### 상담자 소감 · 과정 분석',
      ask: `과정 분석(발언 번호 근거: 잘된 반응, 놓친 지점, 다르게 할 수 있었던 반응 예). 이어서 ${TECHNIQUES_ASK}`,
    },
  ],
  dialogue: [
    {
      key: 'preparation',
      mode: 'fill',
      heading: '### 사실 파악과 준비',
      ask: '만나기 전에 파악한 사실과 준비: 이 내담자를 만나게 된 동기·경위, 사전에 알던 내담자의 상황, 이번 면담의 목표, 몇 번째 만남인지, 만나기 전 상담자의 감정 상태와 기대(상담자 발언·메모·기록에서 드러나는 범위에서)',
    },
    {
      key: 'observation',
      mode: 'fill',
      heading: '### 관찰',
      ask: '전사·메모에서 알 수 있는 범위에서: 만남의 분위기, 내담자의 말투·정서·태도의 변화, 상담자가 예상했던 것과 달랐던 점, 그 자리가 상담자에게 준 인상. 전사·메모로 알 수 없는 장소 모습·옷차림·표정은 쓰지 않는다',
    },
    {
      key: 'analysis',
      mode: 'fill',
      heading: '### 분석',
      ask: '대화가 왜 이렇게 전개되었는지 말·느낌·생각을 연결해 분석: 부각된 주제, 내담자에 대한 의문, 상담자의 반응이 대화에 준 영향, 면담 중 상담자의 모습과 그 이유, 잘한 것·더 잘할 수 있었던 것·못한 것, 처음 목표와 실제 비교, 배운 것. 대화 지점은 P₅, C₇처럼 번호로 인용. 아쉬운 부분도 정직하게',
    },
    {
      key: 'theology',
      mode: 'fill',
      heading: '### 신학적 의미',
      ask: '이 만남의 경험에 부여할 수 있는 의미: 삶과 하나님과의 관계에서 본 궁극적 의미와 가치, 제기되는 신학적 질문과 가능한 응답, 상담자의 신학적 관점 중 도전받은 면과 재확인된 면 (상담자의 감정·생각도 다룸)',
    },
    {
      key: 'plan',
      mode: 'fill',
      heading: '### 앞으로의 계획',
      ask: '이제 어떻게 할 것인가: 내담자에게 도움이 될 것, 도울 수 있는 자원, 다음 만남 시기, 그때의 목표',
    },
    {
      key: 'qaExpected',
      mode: 'add',
      section: '부록',
      sectionTitle: DIALOGUE_APPENDIX,
      label: DIALOGUE_QA_EXPECTED,
      ask: '수업 발표 때 교수님·동료가 이 대화록을 보고 물을 만한 질문 4~6개와 각각의 답변 요지. 한 줄에 하나씩 "질문 — 답변 요지" 형식, 대화 지점은 Pₙ/Cₙ로 인용',
    },
    {
      key: 'qaMine',
      mode: 'add',
      section: '부록',
      sectionTitle: DIALOGUE_APPENDIX,
      label: DIALOGUE_QA_MINE,
      ask: '발표자가 수업에서 교수님·동료에게 던져 피드백을 받을 질문 3~5개 (특정 대화 지점·개입에 대해 묻는 구체적인 질문, Pₙ/Cₙ 인용). 한 줄에 하나씩',
    },
  ],
};

const PLACEHOLDER = /^\[(자료 부족|상담자 직접 작성 필요)[^\]]*\]$/;

/** 이 칸을 AI가 채워도 되는가: 비어 있음 / 자리표시만 있음 / 이전 AI 초안 */
function fillable(body: string[]): boolean {
  const lines = body.map((l) => l.trim()).filter(Boolean);
  return !lines.length || lines.every((l) => PLACEHOLDER.test(l)) || isAiDraftMark(lines[0]!);
}

/** 칸 제목으로 칸 위치 찾기 (제목은 앞부분만 맞으면 됨) */
function findField(lines: string[], heading: string): { start: number; end: number } | null {
  const start = lines.findIndex((l) => l.startsWith(heading));
  if (start < 0) return null;
  let end = start + 1;
  while (end < lines.length && !/^#{1,4}\s/.test(lines[end]!)) end++;
  return { start, end };
}

/** Claude에게 쓸 항목: 문서에 있는 칸 모두 (빈 칸은 채우고, 내용이 있는 칸은 살려서 보완한 전체를 쓴다) */
export function draftableSpecs(
  type: DocumentArtifact['documentType'],
  content: string,
): (DraftSpec & { filled: boolean })[] {
  const lines = content.replace(/\r/g, '').split('\n');
  return DOC_DRAFT_SPECS[type].flatMap((s): (DraftSpec & { filled: boolean })[] => {
    if (s.mode === 'add') return [{ ...s, filled: false }];
    const f = findField(lines, s.heading);
    if (!f) return [];
    const body = lines.slice(f.start + 1, f.end);
    return [{ ...s, filled: !fillable(body) && !isAiDraftMark((body.find((l) => l.trim()) ?? '').trim()) }];
  });
}

export function buildDocDraftPacket(i: {
  type: DocumentArtifact['documentType'];
  title: string;
  /** 현재 문서 본문 (실명·별칭·상담자 이름을 지운 것) */
  content: string;
  /** 회기 전사·요약·근거 등 추가 자료 (식별정보 없음) */
  extras: string;
}): string {
  const specs = draftableSpecs(i.type, i.content);
  const headings = fieldHeadings(i.content);
  return [
    `=== ${DOC_DRAFT_VERSIONS.packet} ===`,
    `[문서] ${i.title}`,
    '[현재 문서 초안] (상: 상담자, 내: 내담자. [자료 부족]/[상담자 직접 작성 필요] 칸을 채워야 함)',
    i.content,
    '',
    '[추가 자료]',
    i.extras,
    '',
    DOC_REQUEST_HEADER,
    DOC_REQUEST_NONE,
    `=== END ${DOC_DRAFT_VERSIONS.packet} ===`,
    '',
    '위 자료만 근거로 아래 항목의 초안을 써라. 상담자가 확인·수정할 초안이다.',
    i.type === 'dialogue'
      ? '- 자료에 없는 사실을 만들지 않는다. 근거가 약한 해석은 "~로 보인다", "~였을 수 있다"처럼 문장 안에서 자연스럽게 표현한다.'
      : '- 자료에 없는 사실을 만들지 않는다. 근거가 약하면 "추정"이라고 쓰고, 정말 자료가 없으면 "자료 부족 — 다음 회기에 확인할 것: …"이라고 쓴다.',
    '- 내담자 진술·상담자 관찰·가설을 구분해 표현한다 (~라고 보고함, ~로 관찰됨, ~일 가능성).',
    '- 가능한 곳은 회기 번호와 발언 번호(예: S2 내5, P3/C3)를 근거로 든다.',
    '- 공식 진단을 확정하지 않는다. 사람 이름 대신 관계로 쓴다.',
    i.type === 'dialogue'
      ? '- 대화록은 상담자의 성찰 기록이다. "나는" 같은 주어를 억지로 넣지 말고 자연스러운 서술문 문단으로 쓴다 (항목 나열 아님). 시제도 자연스럽게 — 면담에서 있었던 일은 과거형, 성찰·의미·계획은 현재형·미래형도 된다. 모든 문장은 앞뒤 문맥이 이어지는 완결된 문장이어야 하며, 전사·메모·기록으로 알 수 없는 내용은 쓰지 않는다. "(보충)", "(확인 필요)", "추정" 같은 메모나 빈칸 표시를 남기지 않는다. 각 항목은 그 칸에 바로 들어갈 내용만.'
      : '- 보고서 문체(~함, ~임). 각 항목은 그 칸에 바로 들어갈 내용만.',
    ...(i.type === 'dialogue'
      ? ['- 대화 지점을 인용할 때는 대화 내용 칸의 번호를 상N → Pₙ, 내N → Cₙ로 바꿔 쓴다 (예: 상6 → P₆, 내5 → C₅).']
      : []),
    '',
    '',
    '쓸 항목 (fields) — 보낸 항목은 그 칸이 보낸 내용으로 바뀐다:',
    ...specs.map(
      (s) => `- ${s.key}: ${s.ask}${s.filled ? ' (지금 내용이 있음 — 그 내용을 살려 보완한 전체를 쓴다)' : ''}`,
    ),
    '',
    '고칠 칸 (revise): 이미 내용이 있는 칸이라도 고치거나 보완·정정할 것이 있으면 (상담자 수정 요청이 있으면 반드시) revise에 넣는다.',
    '- 키는 아래 "칸 제목"을 그대로 쓰고, 값은 그 칸에 들어갈 **고친 뒤의 전체 내용**이다. 앱이 그 칸을 이 내용으로 바꾼다 (일부만 쓰면 나머지가 빠진다).',
    '- 고칠 필요가 없는 칸은 넣지 않는다. 축어록 칸은 "상1: …", "내1: …" 줄 형식을 유지한다.',
    '- 앱이 이 문서의 해당 칸을 직접 바꾼다. 따로 파일을 만들거나 "직접 붙여 넣으라"고 안내하지 않는다.',
    ...(i.type === 'dialogue'
      ? [
          '- 대화록(목회상담 대화록 작성표): 작성표의 여섯 구성 요소(사실 파악과 준비·관찰·면담·분석·신학적 의미·앞으로의 계획)와 부록(발표 예상 질문·수업에서 던질 질문)만 쓴다. 작성표에 없는 항목(기법 정리 등)을 따로 만들지 않는다. 계획대로 되지 않은 상황·상담자의 부족했던 부분도 숨기지 않고 정직하게 쓴다.',
          '- 대화 내용 칸: 발언 문장은 바꾸지 않는다. 전사에 드러난 비언어 단서(웃음·울음 같은 표기, 시각 간격으로 보이는 긴 침묵)만 해당 줄에 괄호로 덧붙여 revise로 보낼 수 있다. 전사로 알 수 없는 표정·몸짓은 지어내지 않는다. 덧붙일 것이 없으면 보내지 않는다. 줄 앞 번호 표기(상1/내1)는 그대로 둔다 (앱이 P₁/C₁로 바꿔 보여준다).',
        ]
      : []),
    '칸 제목:',
    ...headings.map((h) => `- ${h}`),
    '',
    '반드시 아래 형식만 출력한다:',
    BEGIN,
    JSON.stringify({
      formatVersion: DOC_DRAFT_VERSIONS.output,
      fields: Object.fromEntries(specs.map((s) => [s.key, ''])),
      revise: { '칸 제목': '바뀐 전체 내용' },
      cautions: [''],
    }),
    END,
  ].join('\n');
}

/** 상담자가 문서 화면에서 적은 수정 요청이 들어갈 자리 (복사할 때 바꿔 넣는다) */
export const DOC_REQUEST_HEADER = '[상담자 수정 요청]';
export const DOC_REQUEST_NONE = '없음';
export function withDocRequest(packet: string, request: string): string {
  const r = request.trim();
  return r ? packet.replace(`${DOC_REQUEST_HEADER}\n${DOC_REQUEST_NONE}`, `${DOC_REQUEST_HEADER}\n${r}`) : packet;
}

const headingText = (l: string) => l.replace(/^#+\s*/, '').trim();

/** 문서의 모든 칸 제목 (### / ####) */
export function fieldHeadings(content: string): string[] {
  return content
    .replace(/\r/g, '')
    .split('\n')
    .filter((l) => /^#{3,4}\s/.test(l))
    .map(headingText);
}

/** 칸 제목으로 위치 찾기: 제목이 정확히 같은 칸, 없으면 그 제목으로 시작하는 칸 */
function findByHeading(lines: string[], heading: string): { start: number; end: number } | null {
  const want = headingText(heading);
  if (!want) return null;
  let start = lines.findIndex((l) => /^#{3,4}\s/.test(l) && headingText(l) === want);
  if (start < 0) start = lines.findIndex((l) => /^#{3,4}\s/.test(l) && headingText(l).startsWith(want));
  if (start < 0) return null;
  let end = start + 1;
  while (end < lines.length && !/^#{1,4}\s/.test(lines[end]!)) end++;
  return { start, end };
}

/** 칸 내용 (앞뒤 빈 줄 없이). 칸이 없으면 null */
export function fieldBody(content: string, heading: string): string | null {
  const lines = content.replace(/\r/g, '').split('\n');
  const f = findByHeading(lines, heading);
  return f
    ? lines
        .slice(f.start + 1, f.end)
        .join('\n')
        .trim()
    : null;
}

export type DocDraft = { fields: Record<string, string>; cautions: string[]; revise?: Record<string, string> };

export function parseDocDraft(
  raw: string,
  type: DocumentArtifact['documentType'],
): { ok: true; value: DocDraft; warnings: string[] } | { ok: false; errors: string[] } {
  const j = parseJsonLoose(raw);
  if (!j.ok) return { ok: false, errors: [j.error] };
  const o = (j.value && typeof j.value === 'object' ? j.value : {}) as Record<string, unknown>;
  const src = (o.fields ?? o.sections ?? o) as Record<string, unknown>;
  const fields: Record<string, string> = {};
  for (const s of DOC_DRAFT_SPECS[type]) {
    const v =
      str(src[s.key]) ??
      (Array.isArray(src[s.key])
        ? (src[s.key] as unknown[])
            .map((x) => str(x))
            .filter(Boolean)
            .join('\n')
        : undefined);
    if (v) fields[s.key] = v;
  }
  const revise: Record<string, string> = {};
  const rv = o.revise ?? o.revisions;
  const text = (v: unknown) =>
    str(v) ??
    (Array.isArray(v)
      ? v
          .map((x) => str(x))
          .filter(Boolean)
          .join('\n')
      : undefined);
  if (Array.isArray(rv)) {
    for (const x of rv)
      if (x && typeof x === 'object') {
        const r = x as Record<string, unknown>;
        const h = str(r.heading) ?? str(r.title);
        const v = text(r.content ?? r.text ?? r.value);
        if (h && v) revise[h] = v;
      }
  } else if (rv && typeof rv === 'object') {
    for (const [h, v] of Object.entries(rv as Record<string, unknown>)) {
      const t = text(v);
      if (t && h !== '칸 제목') revise[h] = t;
    }
  }
  if (!Object.keys(fields).length && !Object.keys(revise).length)
    return { ok: false, errors: ['초안 내용이 비어 있습니다'] };
  const cautions = Array.isArray(o.cautions) ? o.cautions.map((x) => str(x)).filter((x): x is string => !!x) : [];
  return { ok: true, value: { fields, cautions, ...(Object.keys(revise).length ? { revise } : {}) }, warnings: [] };
}

/**
 * 초안을 문서에 넣는다. Claude가 보낸 칸은 Claude가 쓴 대로 (빈 칸은 채우고, 내용이 있는 칸은 고친다).
 * Claude가 보내지 않은 칸은 그대로. 바뀐 칸에는 AI 초안 표시가 붙고, 이전 내용은 문서 이력에 남는다.
 */
export function applyDocDraft(
  content: string,
  type: DocumentArtifact['documentType'],
  draft: DocDraft,
): {
  content: string;
  applied: string[];
  revised: string[];
  changes: { heading: string; added: number; modified: number }[];
  notFound: string[];
} {
  // 항목 키(fields)로 온 것도 칸 제목으로 바꿔 같은 방식으로 처리한다
  const byHeading: Record<string, string> = {};
  const adds: Extract<DraftSpec, { mode: 'add' }>[] = [];
  for (const s of DOC_DRAFT_SPECS[type]) {
    const text = draft.fields[s.key];
    if (!text) continue;
    if (s.mode === 'fill') byHeading[headingText(s.heading)] = text;
    else adds.push(s);
  }
  for (const [h, t] of Object.entries(draft.revise ?? {})) byHeading[headingText(h)] = t; // revise가 우선
  let out = content.replace(/\r/g, '');
  const applied: string[] = [];
  const revised: string[] = [];
  const changes: { heading: string; added: number; modified: number }[] = [];
  const notFound: string[] = [];
  const put = (heading: string, text: string): 'filled' | 'merged' | 'same' | 'missing' => {
    const lines = out.split('\n');
    const f = findByHeading(lines, heading);
    if (!f) return 'missing';
    const name = headingText(lines[f.start]!);
    const body = lines.slice(f.start + 1, f.end);
    if (fillable(body)) {
      lines.splice(f.start + 1, f.end - f.start - 1, AI_DRAFT_MARK, ...text.trim().split('\n'), '');
      out = lines.join('\n');
      changes.push({ heading: name, added: text.trim().split('\n').length, modified: 0 });
      return 'filled';
    }
    // 내용이 있는 칸도 Claude가 보낸 내용대로 바꾼다 (보낸 칸만. 이전 내용은 문서 이력에 남음)
    const plain = body
      .filter((l) => !isAiDraftMark(l.trim()))
      .join('\n')
      .trim();
    if (plain === text.trim()) return 'same';
    lines.splice(f.start + 1, f.end - f.start - 1, AI_DRAFT_MARK, ...text.trim().split('\n'), '');
    out = lines.join('\n');
    changes.push({ heading: name, added: 0, modified: 1 });
    return 'merged';
  };
  for (const s of DOC_DRAFT_SPECS[type]) {
    if (s.mode !== 'fill' || !draft.fields[s.key]) continue;
    const t = byHeading[headingText(s.heading)]!;
    delete byHeading[headingText(s.heading)];
    const r = put(s.heading, t);
    if (r === 'filled' || r === 'merged') applied.push(s.key);
  }
  for (const [h, t] of Object.entries(byHeading)) {
    const r = put(h, t);
    if (r === 'missing') notFound.push(h);
    else if (r !== 'same') revised.push(h);
  }
  // 서술 칸(add): 없으면 새로 넣고, 있으면 같은 방식으로 비교해 덧붙이기·고치기
  for (const s of adds) {
    const text = draft.fields[s.key]!;
    const ls = out.split('\n');
    if (findField(ls, `### ${s.label}`)) {
      if (put(`### ${s.label}`, text) !== 'same') applied.push(s.key);
    } else {
      out = upsertDraftField(out, s.section, s.label, text, s.after, s.sectionTitle);
      applied.push(s.key);
    }
  }
  return { content: out, applied, revised, changes, notFound };
}

/**
 * Claude가 JSON 대신 보고서 전체(마크다운)를 준 경우: 칸 제목(### / ####)마다 내용을 잘라 revise로 쓴다.
 * 지금 문서에 있는 칸만 쓰고, 내용이 같은 칸은 그대로 둔다 (applyDocDraft에서).
 */
/** 답을 감싸는 줄: BEGIN/END 표식, 코드 울타리 — 칸 본문에 넣지 않는다 */
export const isWrapperLine = (l: string) =>
  /^(BEGIN|END)_COUNSEL_AI_JSON$/.test(l.trim()) || /^```/.test(l.trim());

export function draftFromMarkdown(raw: string, currentContent: string): DocDraft | null {
  const lines = raw.replace(/\r/g, '').split('\n');
  const have = new Set(fieldHeadings(currentContent));
  // 문서의 세부 칸(####)이 어느 칸(###) 아래 있는지: 제목을 조금 바꿔 쓴 세부 칸을 번호로 찾기 위해
  const subsOf = new Map<string, string[]>();
  let docParent = '';
  for (const l of currentContent.replace(/\r/g, '').split('\n')) {
    if (/^###\s/.test(l)) docParent = headingText(l);
    else if (/^####\s/.test(l)) subsOf.set(docParent, [...(subsOf.get(docParent) ?? []), headingText(l)]);
  }
  const num = (h: string) => /^(\d+\))/.exec(h)?.[1];
  const revise: Record<string, string> = {};
  const extra: Record<string, string[]> = {};
  let n = 0;
  let parent = '';
  for (let i = 0; i < lines.length; i++) {
    const level = /^(#{1,4})\s/.exec(lines[i]!)?.[1]!.length;
    if (!level) continue;
    if (level < 3) {
      parent = '';
      continue;
    }
    n++;
    const name = headingText(lines[i]!);
    if (level === 3) parent = name;
    let end = i + 1;
    while (end < lines.length && !/^#{1,4}\s/.test(lines[end]!)) end++;
    const body = lines
      .slice(i + 1, end)
      .filter((l) => !isAiDraftMark(l.trim()) && !isWrapperLine(l))
      .join('\n')
      .trim();
    if (!body) continue;
    if (have.has(name)) {
      revise[name] = body;
      continue;
    }
    if (level === 4 && have.has(parent)) {
      // 제목을 바꿔 쓴 세부 칸: 같은 칸 아래 같은 번호(1), 2)…)의 세부 칸으로
      const same = num(name) && (subsOf.get(parent) ?? []).find((s) => num(s) === num(name));
      if (same) revise[same] = body;
      // 새로 덧붙인 세부 칸(예: '종합'): 위 칸 내용 뒤에 붙인다
      else extra[parent] = [...(extra[parent] ?? []), `${name}:`, body];
    }
  }
  for (const [p, add] of Object.entries(extra)) {
    const base =
      revise[p] ??
      fieldBody(currentContent, p)
        ?.split('\n')
        .filter((l) => !isAiDraftMark(l.trim()))
        .join('\n') ??
      '';
    revise[p] = [base, '', ...add].join('\n').trim();
  }
  return n >= 3 && Object.keys(revise).length ? { fields: {}, cautions: [], revise } : null;
}

/** 승인할 때: "[AI 초안 — 확인 필요]" 표시와 AI 칸 제목의 "(AI 초안)"을 지운다 (내용은 상담자가 확인한 것으로) */
export function stripAiMarks(content: string): string {
  return content
    .split('\n')
    .filter((l) => !isAiDraftMark(l))
    .map((l) => (/^#{3,4}\s/.test(l) ? l.replace(/\s*\(AI 초안\)/, '') : l))
    .join('\n');
}

/** 칸 하나의 내용만 바꾼다 (문서 화면의 칸별 '고치기'). 칸이 없으면 그대로 */
export function setFieldBody(content: string, heading: string, body: string): string {
  const lines = content.replace(/\r/g, '').split('\n');
  const f = findByHeading(lines, heading);
  if (!f) return content;
  lines.splice(f.start + 1, f.end - f.start - 1, ...(body.trim() ? body.trim().split('\n') : []), '');
  return lines.join('\n');
}

/**
 * 칸이 몇 개 안 되는 답(보충 요청에 대한 답 등): 문서에 있는 칸 제목과 같은 제목 아래 본문만 꺼낸다.
 * draftFromMarkdown은 칸이 3개 이상일 때만 문서로 보므로, 그보다 적을 때 쓴다.
 */
export function sectionsByHeadings(raw: string, headings: Iterable<string>): Record<string, string> {
  const have = new Set(headings);
  const lines = raw.replace(/\r/g, '').split('\n');
  const out: Record<string, string> = {};
  for (let i = 0; i < lines.length; i++) {
    if (!/^#{2,4}\s/.test(lines[i]!)) continue;
    const name = headingText(lines[i]!);
    if (!have.has(name)) continue;
    let end = i + 1;
    while (end < lines.length && !/^#{1,4}\s/.test(lines[end]!) && !/END_COUNSEL_AI_JSON/.test(lines[end]!)) end++;
    const body = lines
      .slice(i + 1, end)
      .filter((l) => !isAiDraftMark(l.trim()) && !/BEGIN_COUNSEL_AI_JSON/.test(l))
      .join('\n')
      .trim();
    if (body) out[name] = body;
  }
  return out;
}
